import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { generateWatermarkPreview, generateWatermarkedDocument } from '../../helper/WatermarkHelper';

const WatermarkConfigDialog = ({
  open,
  onClose,
  document: doc,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const [density, setDensity] = useState(1.0);
  const [grainIntensity, setGrainIntensity] = useState(50);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const debounceRef = useRef(null);

  const generatePreview = useCallback(async () => {
    if (!doc?.originalUrl) return;

    setGeneratingPreview(true);
    try {
      const preview = await generateWatermarkPreview(
        doc.originalUrl,
        doc.fileType,
        { density, grainIntensity }
      );
      setPreviewUrl(preview);
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setGeneratingPreview(false);
    }
  }, [doc?.originalUrl, doc?.fileType, density, grainIntensity]);

  // Generate initial preview when dialog opens
  useEffect(() => {
    if (open && doc?.originalUrl) {
      generatePreview();
    }
    // Reset state when dialog closes
    if (!open) {
      setPreviewUrl(null);
      setDensity(1.0);
      setGrainIntensity(50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.originalUrl]);

  // Debounced preview generation on settings change
  useEffect(() => {
    if (!open || !doc?.originalUrl) return;

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounced call
    debounceRef.current = setTimeout(() => {
      generatePreview();
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [density, grainIntensity, open, doc?.originalUrl]);

  const handleGenerate = async () => {
    if (!doc?.originalUrl) return;

    setGenerating(true);
    try {
      const blob = await generateWatermarkedDocument(
        doc.originalUrl,
        doc.fileType,
        { density, grainIntensity }
      );
      onGenerate?.(blob, { density, grainIntensity });
    } catch (error) {
      console.error('Error generating watermark:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleDensityChange = (_, value) => {
    setDensity(value);
  };

  const handleGrainChange = (_, value) => {
    setGrainIntensity(value);
  };

  const densityMarks = [
    { value: 0.3, label: t('watermarkConfig.sparse') },
    { value: 1.0, label: t('watermarkConfig.default') },
    { value: 2.0, label: t('watermarkConfig.dense') },
  ];

  const grainMarks = [
    { value: 0, label: t('watermarkConfig.none') },
    { value: 50, label: t('watermarkConfig.medium') },
    { value: 100, label: t('watermarkConfig.heavy') },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {t('watermarkConfig.title')}
        </Typography>
        <IconButton onClick={onClose} edge="end" disabled={generating}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Preview area */}
        <Box>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            {t('watermarkConfig.preview')}
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 350,
              bgcolor: 'grey.100',
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {generatingPreview ? (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('watermarkConfig.generating')}
                </Typography>
              </Box>
            ) : previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Watermark preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  boxShadow: 2,
                }}
              />
            ) : (
              <Typography color="text.secondary">
                {t('watermarkConfig.noPreview')}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Configuration sliders */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('watermarkConfig.density')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('watermarkConfig.densityHint')}
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={density}
              onChange={handleDensityChange}
              min={0.3}
              max={2.0}
              step={0.1}
              marks={densityMarks}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => value.toFixed(1)}
              disabled={generating}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('watermarkConfig.grain')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('watermarkConfig.grainHint')}
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={grainIntensity}
              onChange={handleGrainChange}
              min={0}
              max={100}
              step={5}
              marks={grainMarks}
              valueLabelDisplay="auto"
              disabled={generating}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="secondary" disabled={generating}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={generating || !doc?.originalUrl}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {generating
            ? t('watermarkConfig.generating')
            : doc?.watermarkedUrl
              ? t('watermarkConfig.regenerate')
              : t('watermarkConfig.generate')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WatermarkConfigDialog;
